import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import * as portalSession from '../../auth/portalSession';

/** Clear stale MSAL interaction state from cookies (from failed auth with undefined tenant). */
const clearStaleMsalCookies = () => {
  document.cookie.split(';').forEach(c => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('msal.') && (name.includes('interaction') || name.includes('undefined'))) {
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  });
};

const field = {
  width: 280, padding: '10px 12px', fontSize: 16, fontFamily: 'inherit',
  border: '1px solid #cfd4da', borderRadius: 6, outline: 'none', boxSizing: 'border-box',
};
const secondary = {
  background: 'none', border: 'none', color: '#cb282b', cursor: 'pointer', fontSize: 15, fontFamily: 'inherit', padding: 6,
};

/**
 * Sign-in page. Two doors:
 *  - Microsoft (LGL staff; externals whose company allows our app)
 *  - E-mail code (invited external users; needs nothing from their IT)
 * The second door only shows when the backend flag EXTERNAL_EMAIL_CODE_LOGIN is on.
 */
const Login = () => {
  const { instance } = useMsal();
  const [codeEnabled, setCodeEnabled] = useState(false);
  const [mode, setMode] = useState('choose'); // choose | email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let alive = true;
    portalSession.isEnabled().then((on) => { if (alive) setCodeEnabled(on); });
    return () => { alive = false; };
  }, []);

  const handleLogin = () => {
    clearStaleMsalCookies();
    instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login redirect error:', e);
    });
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setBusy(true);
    try {
      const { ok, data } = await portalSession.requestCode(email.trim());
      if (!ok) {
        setError(data?.code === 'USE_MICROSOFT'
          ? 'LGL staff sign in with Microsoft — use the button above.'
          : (data?.error || 'Could not send a code. Try again.'));
        return;
      }
      setNotice(`If ${email.trim()} is invited, a 6-digit code is on its way. Check your inbox (and junk folder).`);
      setMode('code');
    } catch (err) {
      setError('Could not reach the portal. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const { ok, data } = await portalSession.verifyCode(email.trim(), code.trim());
      if (!ok) {
        setError(data?.error || 'That code is not valid.');
        if (data?.code === 'CODE_LOCKED') { setMode('email'); setCode(''); }
        return;
      }
      portalSession.start({ token: data.token, expiresIn: data.expiresIn, user: data.user });
      window.location.href = window.location.origin;
    } catch (err) {
      setError('Could not reach the portal. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#f5f6f8',
    }}>
      <img
        src="/lgl-mark.png"
        alt="LGL"
        style={{
          width: 95,
          height: 'auto',
          maxWidth: 'none',
          marginBottom: 5,
          display: 'block',
        }}
      />
      <h1 style={{
        margin: '0 0 13px',
        fontSize: 40,
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: '-0.4px',
        color: '#1a1a1a',
      }}>
        LGL Portal
      </h1>

      {mode === 'choose' && (
        <>
          <button
            type="button"
            onClick={handleLogin}
            style={{
              padding: '10px 25px',
              fontSize: 20,
              fontWeight: 400,
              fontFamily: 'inherit',
              backgroundColor: '#cb282b',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Sign in with Microsoft
          </button>
          {codeEnabled && (
            <button type="button" style={{ ...secondary, marginTop: 18 }} onClick={() => { setError(''); setMode('email'); }}>
              Invited from another company? Sign in with an e-mail code
            </button>
          )}
        </>
      )}

      {mode === 'email' && (
        <form onSubmit={requestCode} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ color: '#444', fontSize: 14, maxWidth: 320, textAlign: 'center' }}>
            Enter the e-mail address LGL invited. We will send a 6-digit code to it.
          </div>
          <input
            style={field} type="email" autoFocus required autoComplete="email"
            placeholder="you@yourcompany.com" value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" disabled={busy || !email.includes('@')} style={{
            padding: '10px 25px', fontSize: 17, fontFamily: 'inherit', backgroundColor: '#cb282b', color: 'white',
            border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, width: 280,
          }}>
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
          {error && <div style={{ color: '#b91c1c', fontSize: 14, maxWidth: 320, textAlign: 'center' }}>{error}</div>}
          <button type="button" style={secondary} onClick={() => { setError(''); setMode('choose'); }}>Back</button>
        </form>
      )}

      {mode === 'code' && (
        <form onSubmit={verifyCode} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          {notice && <div style={{ color: '#166534', fontSize: 14, maxWidth: 340, textAlign: 'center' }}>{notice}</div>}
          <input
            style={{ ...field, letterSpacing: 6, textAlign: 'center', fontSize: 24 }}
            inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus required autoComplete="one-time-code"
            placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button type="submit" disabled={busy || code.length !== 6} style={{
            padding: '10px 25px', fontSize: 17, fontFamily: 'inherit', backgroundColor: '#cb282b', color: 'white',
            border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy || code.length !== 6 ? 0.7 : 1, width: 280,
          }}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          {error && <div style={{ color: '#b91c1c', fontSize: 14, maxWidth: 320, textAlign: 'center' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 14 }}>
            <button type="button" style={secondary} onClick={requestCode} disabled={busy}>Send a new code</button>
            <button type="button" style={secondary} onClick={() => { setError(''); setNotice(''); setCode(''); setMode('email'); }}>Change e-mail</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Login;
